import React, { useEffect, useState } from "react";

import { Navigate, useLocation } from "react-router-dom";
import { Auth } from "./auth";

interface AuthContextType {
  auth: {
    token: string;
    refreshToken: string;
  } | null;
  setAuth: React.Dispatch<
    React.SetStateAction<{
      token: string;
      refreshToken: string;
    } | null>
  >;
  login: (
    username: string,
    password: string,
    secret?: string
  ) => Promise<boolean>;
  signout: () => void;
  resetPassword: (password: string, token: string) => Promise<boolean>;
  organizationId: string | null;
  setOrganizationId: (id: string | null) => void;
  isLoading: boolean; // Add this line
}

let AuthContext = React.createContext<AuthContextType>(null!);

const AuthApi = new Auth();

export function AuthProvider({ children }: { children: React.ReactNode }) {
  let [auth, setAuth] = useState<{
    token: string;
    refreshToken: string;
  } | null>(null);

  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Try to restore the auth state from local storage when the app loads
    const loadAuth = async () => {
      setIsLoading(true);
      let storedAuth = localStorage.getItem("auth-dashboard");
      if (storedAuth) {
        setAuth(JSON.parse(storedAuth));
      }
      setIsLoading(false);
    };
    loadAuth();
  }, []);

  useEffect(() => {
    // Save the auth state to local storage whenever it changes
    if (auth === null) {
      localStorage.removeItem("auth");
    }
    if (auth) {
      localStorage.setItem("auth-dashboard", JSON.stringify(auth));
    }
  }, [auth]);

  let login = async (username: string, password: string, secret?: string) => {
    try {
      const auth = await AuthApi.login(username, password, secret);
      setAuth(auth);
      return true;
    } catch (e: any) {
      console.log(e?.message);
      if (e?.message === "MFA required") {
        throw new Error(e?.message);
      }

      if (e?.message === "MFA failed") {
        throw new Error(e?.message);
      }

      // if (e instanceof AxiosError) {
      //   if (e.response?.status === 401) {
      //     return false;
      //   }
      //   console.error(`LOGIN request: ${e.message}`, e.toJSON());
      // }
      return false;
    }
  };

  let signout = () => {
    setAuth(null);
    setOrganizationId(null); // Clear organization when signing out
    localStorage.removeItem("auth-dashboard");
  };

  let resetPassword = async (password: string, token: string) => {
    try {
      await AuthApi.resetPassword(password, token);
      return true;
    } catch {
      return false;
    }
  };

  let value = {
    auth,
    setAuth,
    login,
    signout,
    resetPassword,
    organizationId,
    setOrganizationId,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return React.useContext(AuthContext);
}

export function RequireAuth({ children }: { children: JSX.Element }) {
  let auth = useAuth();
  let location = useLocation();

  if (auth.isLoading) {
    // You can return a loading spinner or null here
    return <div>Loading...</div>;
  }

  if (!auth.auth) {
    // Only redirect if we're sure auth is not null
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}
